import {
  type ConnectionLineComponentProps,
  type Edge,
  type EdgeProps,
  getSmoothStepPath
} from '@xyflow/react'
import { motion } from 'framer-motion'
import { memo } from 'react'

/** How a line looks: at rest, lit by the builder, carrying a run, already travelled, or a road not taken. */
export type EdgeState = 'idle' | 'lit' | 'flowing' | 'done' | 'skipped'
export type FlowEdgeData = { state?: EdgeState; fresh?: boolean; error?: boolean; color?: string }
export type FlowEdge = Edge<FlowEdgeData, 'flow'>

const FLAG = '#ef4444'

/** Real colours, not CSS variables: the arrowheads are SVG markers and need a plain colour. */
export function edgeColor(state: EdgeState, error: boolean, dark: boolean) {
  if (error && state !== 'done') return FLAG
  if (state === 'lit') return '#f59e0b'
  if (state === 'done') return '#10b981'
  if (state === 'flowing') return dark ? '#fafafa' : '#171717'
  return dark ? '#525252' : '#a3a3a3'
}

const PARTICLES = [0, 1, 2]

function FlowEdgeView({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  data
}: EdgeProps<FlowEdge>) {
  const [path] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 18
  })
  const state = data?.state ?? 'idle'
  const error = !!data?.error
  const color = data?.color ?? '#a3a3a3'
  const live = state === 'flowing' || state === 'lit'
  const dashed = error || state === 'skipped'
  const pathId = `wf-edge-${id}`

  return (
    <g>
      {live && (
        <path
          d={path}
          fill="none"
          style={{ stroke: color, strokeOpacity: 0.16, strokeWidth: 12, strokeLinecap: 'round' }}
        />
      )}
      <motion.path
        id={pathId}
        d={path}
        fill="none"
        className="react-flow__edge-path"
        markerEnd={markerEnd}
        style={{
          stroke: color,
          strokeWidth: selected ? 3 : live ? 2.5 : 2,
          opacity: state === 'skipped' ? 0.35 : 1,
          ...(dashed ? { strokeDasharray: '6 6' } : {})
        }}
        // A new line draws itself from its start to its end. Dashed lines skip it: drawing uses the dash pattern.
        initial={data?.fresh && !dashed ? { pathLength: 0 } : false}
        animate={dashed ? undefined : { pathLength: 1 }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      />
      <path d={path} fill="none" className="react-flow__edge-interaction" strokeOpacity={0} strokeWidth={22} />
      {state === 'flowing' &&
        PARTICLES.map((i) => (
          <circle key={i} r={4} opacity={0} style={{ fill: color }}>
            <animateMotion
              dur="1.5s"
              begin={`${i * 0.5}s`}
              repeatCount="indefinite"
              calcMode="spline"
              keyPoints="0;1"
              keyTimes="0;1"
              keySplines="0.45 0 0.2 1"
            >
              <mpath href={`#${pathId}`} />
            </animateMotion>
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.15;0.8;1"
              dur="1.5s"
              begin={`${i * 0.5}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
    </g>
  )
}

export const edgeTypes = { flow: memo(FlowEdgeView) }

/** The line you pull while connecting: dashes march toward the pointer, and the end swells when it
 * snaps onto a step it can connect to. */
export function ConnectionLine({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
  connectionStatus
}: ConnectionLineComponentProps) {
  const [path] = getSmoothStepPath({
    sourceX: fromX,
    sourceY: fromY,
    sourcePosition: fromPosition,
    targetX: toX,
    targetY: toY,
    targetPosition: toPosition,
    borderRadius: 18
  })
  const valid = connectionStatus === 'valid'
  const color = valid ? '#10b981' : 'var(--wf-ink)'
  return (
    <g>
      <path d={path} fill="none" style={{ stroke: color, strokeWidth: 2.5, strokeDasharray: '7 6' }}>
        <animate attributeName="stroke-dashoffset" values="13;0" dur="0.6s" repeatCount="indefinite" />
      </path>
      <motion.circle
        cx={toX}
        cy={toY}
        animate={{ r: valid ? 9 : 5 }}
        transition={{ type: 'spring', stiffness: 500, damping: 18 }}
        style={{ fill: color, fillOpacity: valid ? 0.25 : 1, stroke: color, strokeWidth: 2 }}
      />
    </g>
  )
}
